import { useEffect, useState } from 'react'
import { useTranslation } from '../../i18n/I18nProvider'
import { formatCountdown } from '../../i18n/format'
import { Bidi } from '../Bidi'
import type { AuthScreenProps } from './types'

type AnyAuthError =
  | NonNullable<AuthScreenProps['phoneStep']['error']>
  | NonNullable<AuthScreenProps['codeStep']['error']>
  | NonNullable<AuthScreenProps['nameStep']['error']>

const KEY = {
  'invalid-phone': 'invalidPhone',
  'too-many-codes': 'tooManyCodes',
  // D248 — the client's own 30 s cooldown, refusing a re-submission of the number it was
  // issued for. Never a server answer; the platform has no 30 s tier.
  'resend-blocked': 'resendBlocked',
  'wrong-code': 'wrongCode',
  'name-required': 'nameRequired',
  'invalid-name': 'invalidName',
  network: 'networkError',
  server: 'serverError',
  unavailable: 'serviceUnavailable',
  // R1-7(f): a distinct string from `serviceUnavailable` on purpose — this one names the
  // device's local store, not the server, and "try again" would be dishonest for it.
  storage: 'storageUnavailable',
} as const satisfies Record<AnyAuthError, string>

/**
 * Seconds remaining until `target`, ticking once a second, `undefined` once it is past.
 *
 * **Read off the clock on every tick rather than decremented**, which is what makes it correct
 * after a backgrounded screen, a resumed one, or a browser that throttled the interval — the
 * three objections the docblock below used to raise against having a countdown at all.
 */
function useCountdown(target: string | undefined): number | undefined {
  const releaseAt = target === undefined ? Number.NaN : Date.parse(target)
  const valid = Number.isFinite(releaseAt)
  const [left, setLeft] = useState(() =>
    valid ? Math.max(0, Math.ceil((releaseAt - Date.now()) / 1000)) : 0,
  )

  useEffect(() => {
    if (!valid) return undefined
    let timer = 0
    const apply = () => {
      const next = Math.max(0, Math.ceil((releaseAt - Date.now()) / 1000))
      setLeft(next)
      // A refusal that has released has nothing left to count, and this component stays mounted
      // behind whatever error replaces it.
      if (next === 0 && timer !== 0) window.clearInterval(timer)
    }
    apply()
    timer = window.setInterval(apply, 1000)
    return () => window.clearInterval(timer)
  }, [releaseAt, valid])

  if (!valid || left <= 0) return undefined
  return left
}

/**
 * The cap refusal's copy: **a live countdown, or nothing.**
 *
 * **D248 reversed this docblock's "never a live countdown" clause, and answered its objection
 * rather than overruling it.** The three original arguments, and where each stands:
 *
 * *"A ticking number would have to keep ticking while the screen is backgrounded, be right
 * after a resume, and survive a clock the device sets itself."* — **Answered by the mechanism.**
 * The server sends `retryAt` as an absolute instant and always has; `useCountdown` recomputes
 * the remainder from it on every tick, so a backgrounded screen resumes correct by construction
 * rather than by counting ticks nobody delivered. A duration is derived from the fact; the fact
 * is still what crossed the wire.
 *
 * *"It is not a counted string, and that is what makes it translatable."* — **Untouched, and
 * binding.** `formatCountdown` renders a digit clock (`14:59`, `4:52:10`), so no noun agrees
 * with a number and Russian's 1 / 2–4 / 5+ problem does not arise (`adding-a-locale.md` §3).
 * **"try again in {{n}} minutes" is still forbidden**, and for exactly the original reason.
 *
 * *"A day it cannot name is a day it does not claim."* — **Kept, as the fallback.** An absent,
 * unparseable, or already-past `retryAt` still renders the timeless string; nothing invents a
 * number it was not given.
 *
 * What went with the reversal is the pair of wall-clock keys. `tooManyCodesAt` and
 * `tooManyCodesTomorrow` existed to place a wait on a calendar, which is a job a countdown does
 * better at every duration this cap can produce — D248 cut the ceiling window to five hours, so
 * there is no longer a wait long enough for "tomorrow at 07:40" to beat "4:52:10".
 */
/**
 * One inline refusal line for every auth step.
 *
 * It renders **a label, chosen by a code the caller was given** — never a message the server
 * composed and never the value that was rejected (D57 item 6, and `ValidationError`'s own
 * never-echo rule). A new refusal is a new code plus a catalog key in all five locales, which
 * is what keeps the OTP surface from growing an untranslated English string under pressure.
 *
 * **`too-many-codes` is the one code that picks between three keys**, on a time the server
 * sent. That is still one code and still a label chosen here: `retryAt` is an instant, not
 * copy, and the sentence around it belongs to the catalogs exactly as the other ten do.
 */
export function AuthError({
  error,
  retryAt,
  resendInSeconds,
}: {
  readonly error: AnyAuthError | undefined
  /** Only `too-many-codes` reads it; every other code ignores it. */
  readonly retryAt?: string | undefined
  /** Only `resend-blocked` reads it — the client-side cooldown's own remainder. */
  readonly resendInSeconds?: number | undefined
}): JSX.Element | null {
  const { t } = useTranslation('auth')
  // Hooks run before any early return: `error` changes between renders, and a countdown
  // started under one refusal must not become a conditional hook under the next.
  const capLeft = useCountdown(error === 'too-many-codes' ? retryAt : undefined)

  if (error === undefined) return null

  /*
   * **The countdown is a separate element, bidi-isolated, appended after the sentence.**
   *
   * `{{time}}` sits at the end of every one of these strings in all five locales, so the
   * placeholder is interpolated empty and the digits follow as their own node — `CodeStep`'s
   * own pattern for the 30 s timer, and the reason a `4:52:10` does not reorder inside an RTL
   * line. Interpolating the digits directly would put them at the mercy of the bidi algorithm.
   */
  const countdown = (key: 'tooManyCodesIn' | 'resendBlocked', left: number): JSX.Element => (
    <div className="hint error countdown">
      {t(key, { time: '' })}
      <Bidi>{formatCountdown(left)}</Bidi>
    </div>
  )

  if (error === 'too-many-codes' && capLeft !== undefined) return countdown('tooManyCodesIn', capLeft)
  if (error === 'resend-blocked') {
    // No remainder means the wait has already elapsed, and a refusal with nothing left to
    // count is not a refusal worth a line — the next submit will go through.
    if (resendInSeconds === undefined || resendInSeconds <= 0) return null
    return countdown('resendBlocked', resendInSeconds)
  }
  return <div className="hint error">{t(KEY[error])}</div>
}
